import {isCompletionChunk, type LogEntry, type ReplyToCommentToolArgs} from '../../../types/session'
import {useLogs} from '../../../hooks/useLogs'
import {useCurrentUser} from '../../../contexts/CurrentUserContext'
import {CopilotMessageBubble, CopilotMessageContainer, CopilotMessageLoading} from './CopilotMessageBubble'

interface CopilotReplyMessageProps {
  logsPollingInterval: number
}

export function CopilotReplyMessage({logsPollingInterval}: CopilotReplyMessageProps) {
  const {currentUserIsSessionOwner} = useCurrentUser()
  const {
    data: logsData,
    isLoading: logsIsLoading,
    error: logsError,
  } = useLogs({logsPollingInterval, currentUserIsSessionOwner})

  const replyToCommentCalls = extractReplyToCommentCalls(logsData)

  if (logsIsLoading) {
    return <CopilotMessageLoading />
  }

  if (logsError || replyToCommentCalls.length === 0) {
    return null
  }

  return (
    <CopilotMessageContainer>
      {replyToCommentCalls.map(call => (
        <CopilotMessageBubble key={call.comment_id} markdown={call.reply} />
      ))}
    </CopilotMessageContainer>
  )
}

function extractReplyToCommentCalls(logsData: LogEntry[] = []) {
  const replyToCommentCallsMap = new Map<string, ReplyToCommentToolArgs>()

  for (const logEntry of logsData) {
    if (isCompletionChunk(logEntry)) {
      for (const choice of logEntry.choices) {
        if (choice.delta.role !== 'assistant') {
          continue
        }
        const toolCalls = choice.delta.tool_calls || []
        for (const toolCall of toolCalls) {
          const toolName = toolCall.function?.name ?? toolCall.custom?.name
          const rawArguments = toolCall.function?.arguments ?? toolCall.custom?.input
          if (toolName !== 'reply_to_comment') {
            continue
          }
          if (!rawArguments) {
            continue
          }
          try {
            const args =
              typeof rawArguments === 'string'
                ? (JSON.parse(rawArguments) as ReplyToCommentToolArgs)
                : (rawArguments as ReplyToCommentToolArgs)
            replyToCommentCallsMap.set(logEntry.id, args)
          } catch {
            // Fall back to raw string for custom tools whose arguments are not JSON
            if (typeof rawArguments === 'string') {
              replyToCommentCallsMap.set(logEntry.id, {reply: rawArguments} as ReplyToCommentToolArgs)
            }
            // Skip malformed tool calls
          }
        }
      }
    }
  }

  return [...replyToCommentCallsMap.values()]
}
